import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { UserResponse, UserCreateRequest, UserUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EnumToComboValues, EntityUtils } from '../utils';
import { UserDetailsSectionHandler } from './details.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { AssetGroupingService, AuthService, nullAccessDenied } from 'app/services';
import { UserNotiftySectionHandler } from './notify.section';
import { UserRolesSectionHandler } from './roles.section';
import { OidcTagsSectionHandler } from './oidc-tags.section';
import { OidcAuthService } from 'app/services/oidc/oidc.service';
import { ModalService } from 'app/shared/components/modal';
import { buildChartSectionGroup } from '../shared/chart.section';
import { UserApiKeysSectionHandler } from './apikeys.section';
import { UserClientPermissionsSectionHandler } from './client-permissions.section';
import { UserAssetPermissionsSectionHandler } from './asset-permissions.section';
import { UserOtpSectionHandler } from './otp.section';

@RegisterEntityHandler('user')
@Injectable()
export class UserHandler extends EntityHandler<UserResponse> {

    constructor(
        admin: AdminEditorService,
        private auth: AuthService,
        private grouping: AssetGroupingService,
        private oidcAuthService: OidcAuthService,
        private modal: ModalService
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'suspended', 'deleted'];

    actions = [
        { icon: 'key', name: this.admin.translate('EDITORS.USER.RESET.TIP'), click: (user) => this.resetUser(user) },
    ];


    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listUsers/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.accounts.listUsers(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'user',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.USERS'),
        }));
    }

    getEntity(id: string): Promise<UserResponse> {
        return this.admin.getCached(`getUser/${id}`, api => api.accounts.getUser(id), 10);
    }

    createEntity(ownerId: string, entity: UserCreateRequest): Promise<UserResponse> {
        return this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId)).then(company => {
            entity.ownerId = ownerId;
            entity.state = 'active';
            if (company.type === 'client') {
                entity.defaultClient = { id: company.id, name: company.name };
            }
            return this.admin.applyUpdate(api => api.accounts.createUser(entity)).then(result => {
                if (result.inviteError) {
                    setTimeout(() => { // needs a timeout as we're already in a modal and have to wait for it to close
                        this.admin.modal.error(this.admin.translate('EDITORS.USER.INVITE.ERROR_EMAIL'), result.inviteError as any);
                    }, 500);
                }
                return result;
            });
        });
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.accounts.updateUser(entity.id, { state: state as any }));
    }

    async resetUser(entity: UserResponse) {
        const result = await this.admin.modal.confirm(this.admin.translate('EDITORS.USER.RESET.CONFIRM'));
        if (result) {
            try {
                const user = await this.admin.applyUpdate(api => api.accounts.resetUser(entity.id, { resetUrl: 'https://{domain}/#/login?token={token}' }));
                if (user.resetError) {
                    setTimeout(() => { // needs a timeout as we're already in a modal and have to wait for it to close
                        this.admin.modal.error(this.admin.translate('EDITORS.USER.RESET.ERROR_EMAIL'), user.resetError as any);
                    }, 500);
                }

            } catch (err) {
                this.admin.modal.error(this.admin.translate('EDITORS.USER.RESET.ERROR'), err);
            }
        }
    }

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {
        return Promise.all([
            this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId)),
            this.grouping.getCostCentres(ownerId),
        ]).then(([company, costCentres]) => {

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.USER.COLUMNS');
            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', ICONS.user),
                grid.TextColumn('emailAddress', 'EMAIL_ADDRESS'),
                grid.TextColumn('mobile', 'MOBILE_NUMBER', 110),
                grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
                company.type === 'client' ? grid.IdNameComboColumn('costCentre', 'COST_CENTRE', EntityUtils.treeToComboFilter(costCentres), 150) : null,
                grid.TextColumn('timeZoneId', 'TIMEZONE'),
                grid.TextColumn('language', 'LANGUAGE', 80),
                grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
            ].filter(x => x);
            return columns;
        });
    }

    async getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<UserResponse>[]> {

        const issuers = await OidcTagsSectionHandler.getIssuers(this.admin, ownerId);
        const roles = await this.admin.getCached(`listUserRoles/${ownerId}`, api => api.accounts.listUserRoles(ownerId)).catch(nullAccessDenied);

        const sections: SectionHandlerGroup<UserResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new UserDetailsSectionHandler(this.admin, this.grouping, ownerId, false),
                roles && new UserRolesSectionHandler(this.admin, ownerId),
                Object.keys(issuers).length > 0 ? new OidcTagsSectionHandler(this.admin, ownerId, this.oidcAuthService, this.modal) : null,
                entity?.owner?.type === 'vendor' ? new UserClientPermissionsSectionHandler(this.admin, ownerId) : null,
                entity?.owner?.type === 'client' && this.admin.user.owner.type === 'system' ?
                    new UserAssetPermissionsSectionHandler(this.admin, ownerId) : null,
                this.admin.user.owner.type === 'system' ? new UserOtpSectionHandler(this.admin, this.auth, ownerId) : null,
                new UserApiKeysSectionHandler(this.admin, ownerId),
                new UserNotiftySectionHandler(this.admin, ownerId),
            ].filter(x => x),
        }];

        if (this.admin.user.owner.type === 'system') {
            sections.push(buildChartSectionGroup(this.admin, 'user', ['minute'], ['24h', '7d', '30d', '60d', '90d']));
        }

        return sections;
    }

    async getCreationSections(ownerId: string): Promise<{ entityType: string, entity: UserUpdateRequest, sections: FormSectionHandler<UserResponse, any>[] }> {
        // this will throw if user is not allowed to list user roles, which is what we want
        await this.admin.getCached(`listUserRoles/${ownerId}`, api => api.accounts.listUserRoles(ownerId));
        return {
            entityType: 'user',
            entity: {}, // set up some defaults if you want
            sections: [
                new UserDetailsSectionHandler(this.admin, this.grouping, ownerId, true),
                new UserRolesSectionHandler(this.admin, ownerId),
            ],
        };
    }

}












